import { ApiService, ResumeSubscriptionResponseStatusEnum } from 'js/src/client';
import { HTTP_STATUS_CODES } from 'js/src/libs/consts';
import { waitForMillis } from 'js/src/libs/custom-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import PaymentService from 'js/src/libs/payment-service';
import { isApiErrorWithProperty } from 'js/src/types';

export type ResumeSubscriptionOutcome =
    | { status: 'resumed' }
    // the customer's card or bank stopped the charge; `message` tells them what to do
    | { status: 'payment_failed'; message: string }
    // the bank approved it but Stripe is still settling the charge; renders stay blocked until it does
    | { status: 'payment_pending'; message: string }
    | { status: 'error' };

const FINISH_RESUME_ATTEMPTS = 5;
const FINISH_RESUME_RETRY_MS = 1000;
const PAYMENT_PENDING_MESSAGE =
    "We're still confirming your payment with your bank. Try publishing again in a minute.";

const AUTHENTICATION_FAILED_MESSAGE =
    "Your bank didn't confirm the payment, so your subscription is still paused. Please try again or use a different card.";

/**
 * After the customer passes 3D Secure, ask the backend to finish the resume. Until Stripe has
 * settled the charge (the PaymentIntent can briefly be `processing`) it keeps answering
 * `requires_action` and keeps renders blocked, so retry for a few seconds rather than claiming
 * the subscription is resumed.
 */
async function finishResume(): Promise<ResumeSubscriptionOutcome> {
    for (let attempt = 1; attempt <= FINISH_RESUME_ATTEMPTS; attempt++) {
        const response = await ApiService.pricingSubscriptionResumeCreate();
        if (response.data.status === ResumeSubscriptionResponseStatusEnum.SUCCESS) {
            return { status: 'resumed' };
        }
        if (attempt < FINISH_RESUME_ATTEMPTS) {
            await waitForMillis(FINISH_RESUME_RETRY_MS);
        }
    }
    return { status: 'payment_pending', message: PAYMENT_PENDING_MESSAGE };
}

/**
 * Resume a paused subscription. Once a pause is in effect, resuming charges for a new period; if the
 * card needs 3D Secure the backend asks for it, so this runs Stripe's confirmation step and then
 * calls resume again to finish.
 */
export async function resumePausedSubscription(): Promise<ResumeSubscriptionOutcome> {
    try {
        const response = await ApiService.pricingSubscriptionResumeCreate();
        if (response.data.status !== ResumeSubscriptionResponseStatusEnum.REQUIRES_ACTION) {
            return { status: 'resumed' };
        }

        const clientSecret = response.data.client_secret;
        if (!clientSecret) {
            ErrorReporter.capture(
                new Error('Resume requires authentication but client_secret is missing'),
            );
            return { status: 'error' };
        }

        const confirmation = await PaymentService.confirmCardPayment(clientSecret);
        if (confirmation.error) {
            return {
                status: 'payment_failed',
                message: confirmation.error.message ?? AUTHENTICATION_FAILED_MESSAGE,
            };
        }

        return await finishResume();
    } catch (error: unknown) {
        if (
            isApiErrorWithProperty(error, 'reason') &&
            error.status === HTTP_STATUS_CODES.PAYMENT_REQUIRED &&
            typeof error.body.reason === 'string'
        ) {
            return { status: 'payment_failed', message: error.body.reason };
        }
        ErrorReporter.capture(error);
        return { status: 'error' };
    }
}
